import type { TabView } from "@e308/ux";
import { REPEATABLES, SPECIALIZATIONS, TECHNOLOGIES } from "../content/research.js";
import type { TechnologyEffect } from "../content/types.js";
import { repeatableCost, researchLegal } from "../research-state.js";
import type { ResearchOrder, ResearchState } from "../research-types.js";
import type { AppIntent, AppState } from "./app-source.js";
import { dashboard, help } from "./layout-view.js";
import { gameIntent, heading, type AppNode as Node, name, text } from "./view-nodes.js";

export function researchTab(state: AppState): TabView<AppIntent, string> {
  const research = state.observation?.self.research as ResearchState;
  return {
    id: "research",
    label: "Research",
    content: dashboard([
      heading("research-heading", "Research"),
      help(
        "research-help",
        "Planning research",
        "Research uses stored RP and game time. Queue an available technology to begin when enough RP is saved. Read prerequisites on each card. Industrial and military specializations are permanent: you may choose only one of each.",
      ),
      text("research-stock", `${state.observation?.self.researchPoints ?? "0"} RP available.`),
      activeResearch(research, state.observation?.gameTimeMs ?? 0),
      researchQueue(research),
      ...(["Industry", "Army", "Defense"] as const).flatMap((category) => [
        heading(`research-${category}`, category, 3),
        ...TECHNOLOGIES.filter((entry) => technologyCategory(entry.id) === category).map((entry) =>
          researchChoice(
            research,
            { id: `choice-${entry.id}`, kind: "technology", target: entry.id },
            entry.name,
            entry.rpCost,
            entry.durationSeconds,
            entry.prerequisite
              ? `Requires ${technologyName(entry.prerequisite)}.`
              : "No prerequisite.",
            effectText(entry.effect),
          ),
        ),
      ]),
      heading("specializations-heading", "Permanent specializations", 3),
      ...SPECIALIZATIONS.map((entry) =>
        researchChoice(
          research,
          { id: `choice-${entry.id}`, kind: "specialization", target: entry.id },
          entry.name,
          entry.rpCost,
          entry.durationSeconds,
          `Requires ${entry.prerequisites.map(technologyName).join(" and ")}; choose one ${entry.branch} specialization.`,
          effectText(entry.effect),
          `Choose ${entry.name} permanently? The other ${entry.branch} specializations become unavailable.`,
        ),
      ),
      heading("repeatables-heading", "Long-match improvements", 3),
      ...REPEATABLES.map((entry) => {
        const level = research.repeatableLevels[entry.id];
        return researchChoice(
          research,
          { id: `choice-${entry.id}`, kind: "repeatable", target: entry.id },
          `${entry.name} — next level ${level + 1}`,
          repeatableCost(entry.id, level).toString(),
          entry.durationSeconds,
          `Requires ${technologyName(entry.prerequisite)}.`,
          `Each level multiplies ${entry.targets.map(name).join(", ")} by ×${entry.factorPerLevel}.`,
        );
      }),
    ]),
  };
}

function activeResearch(research: ResearchState, gameTimeMs: number): Node {
  const active = research.active;
  if (!active) return text("research-active", "No active research.");
  return {
    kind: "infobox",
    id: "research-active",
    title: `Active: ${choiceName(active.order)}`,
    initiallyOpen: true,
    content: [
      text(
        "research-active-time",
        `${Math.max(0, Math.ceil((active.completesAtMs - gameTimeMs) / 1_000))} game seconds remain; ${active.reservedRp} RP reserved.`,
      ),
      cancelAction(active.order, active.reservedRp),
    ],
  };
}

function researchQueue(research: ResearchState): Node {
  return {
    kind: "infobox",
    id: "research-queue",
    title: `Research queue — ${research.queue.length}`,
    initiallyOpen: true,
    content:
      research.queue.length === 0
        ? [text("research-queue-empty", "The research queue is empty.")]
        : research.queue.flatMap((order, index) => [
            text(
              `${order.id}-summary`,
              `${index + 1}. ${choiceName(order)}${researchLegal(research, order) ? "" : " — waiting for prerequisite or branch availability"}`,
            ),
            cancelAction(order, "0"),
          ]),
  };
}

function researchChoice(
  state: ResearchState,
  order: ResearchOrder,
  label: string,
  cost: number | string,
  durationSeconds: number,
  prerequisite: string,
  effect: string,
  confirm?: string,
): Node {
  const completed = order.kind === "technology" && state.completed.includes(order.target);
  const selected =
    order.kind === "specialization" &&
    (state.industrialSpecialization === order.target ||
      state.militarySpecialization === order.target);
  const pending = [state.active?.order, ...state.queue].some(
    (item) => item?.kind === order.kind && item.target === order.target,
  );
  const legal = researchLegal(state, order);
  const enabled = legal && !pending && !completed && !selected;
  return {
    kind: "action",
    id: order.id,
    action: {
      id: order.id,
      label: completed || selected ? `${label} — complete` : pending ? `${label} — queued` : label,
      description: [{ kind: "text", value: `${prerequisite} ${effect}` }],
      enabled,
      intent: gameIntent(order.id, {
        kind: "enqueue-research",
        researchKind: order.kind,
        target: order.target,
      }),
      blockers: enabled
        ? []
        : [{ kind: "disabled", actionId: order.id, reasonKey: choiceBlocker(state, order) }],
      costs: [{ resourceId: "research-points", label: "RP", value: String(cost) }],
      eta: { kind: "finite", ms: durationSeconds * 1_000, clock: "game" },
      ...(confirm ? { confirm } : {}),
    },
  };
}

function cancelAction(order: ResearchOrder, reservedRp: string): Node {
  const refund =
    reservedRp === "0"
      ? "No RP is reserved for this queued item."
      : "75% of reserved RP is refunded.";
  return {
    kind: "action",
    id: `cancel-${order.id}`,
    action: {
      id: `cancel-${order.id}`,
      label: `Cancel ${choiceName(order)}`,
      enabled: true,
      intent: gameIntent(`cancel-${order.id}`, { kind: "cancel-research", orderId: order.id }),
      blockers: [],
      confirm: `Cancel ${choiceName(order)}? ${refund} Elapsed progress is discarded.`,
    },
  };
}

function effectText(effect: TechnologyEffect): string {
  if (effect.kind === "unlock") return `Unlocks ${effect.targets.map(name).join(", ")}.`;
  if (effect.kind === "multiply")
    return `Multiplies ${effect.targets.map(name).join(", ")} by ×${effect.factor}.`;
  if (effect.kind === "restore-metal-ratio")
    return `Restoration Metal cost becomes ×${effect.ratio} of new cost.`;
  return `${name(effect.target)} becomes ${effect.ratio}.`;
}

function choiceName(order: ResearchOrder): string {
  return (
    [...TECHNOLOGIES, ...SPECIALIZATIONS, ...REPEATABLES].find((entry) => entry.id === order.target)
      ?.name ?? name(order.target)
  );
}

function technologyName(id: string): string {
  return TECHNOLOGIES.find((entry) => entry.id === id)?.name ?? name(id);
}

function choiceBlocker(state: ResearchState, order: ResearchOrder): string {
  if (order.kind === "technology" && state.completed.includes(order.target)) return "completed";
  if (order.kind === "specialization") {
    const entry = SPECIALIZATIONS.find((item) => item.id === order.target);
    if (entry?.branch === "industrial" && state.industrialSpecialization)
      return "industrial-choice-made";
    if (entry?.branch === "military" && state.militarySpecialization) return "military-choice-made";
  }
  return "prerequisite-not-met";
}

function technologyCategory(id: string): "Industry" | "Army" | "Defense" {
  if (/robot|siege|deployment|assembly/.test(id)) return "Army";
  if (/shield|supply|armor/.test(id)) return "Defense";
  return "Industry";
}
